/**
 * 既存ブレースへの設置スナップ。論理辺の両端・中点から、次の間隔が snap
 * 以上のあいだ 2 分割を繰り返す。円弧は弧長。押し出し中は帯上の格子。
 */

import {
  ZERO_PLANE_THICKNESS,
  effectivePlaneThickness,
  braceBendCenter,
  braceCrossSectionBasis,
  braceExtrudeOffset,
  bracePolyline,
  vec,
} from './geometry';
import type { BraceArc, BraceGeometry, Vec3 } from './types';

export interface BraceSnapLine {
  a: Vec3;
  b: Vec3;
}

const POINT_EPS = 1e-6;

function rotateAroundAxis(v: Vec3, axis: Vec3, angle: number): Vec3 {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const crossed = vec.cross(axis, v);
  const along = vec.dot(axis, v);
  return vec.add(vec.add(vec.scale(v, cos), vec.scale(crossed, sin)), vec.scale(axis, along * (1 - cos)));
}

function samePoint(a: Vec3, b: Vec3): boolean {
  return vec.length(vec.sub(a, b)) < POINT_EPS;
}

function pushUnique(points: Vec3[], point: Vec3): void {
  if (points.some((existing) => samePoint(existing, point))) return;
  points.push(point);
}

function polylineArcCenter(arcs: BraceArc[] | undefined, segmentIndex: number): Vec3 | undefined {
  return arcs?.find((arc) => arc.segment === segmentIndex)?.center;
}

interface ArcFrame {
  center: Vec3;
  axis: Vec3;
  from: Vec3;
  angle: number;
  radius: number;
}

function segmentArc(start: Vec3, end: Vec3, hint: Vec3 | undefined): ArcFrame | null {
  if (!hint) return null;
  const center = braceBendCenter(start, end, hint);
  if (!center) return null;
  const from = vec.sub(start, center);
  const to = vec.sub(end, center);
  const cross = vec.cross(from, to);
  const axis = vec.normalize(cross);
  if (vec.length(axis) < 1e-9) return null;
  const radius = vec.length(from);
  if (radius < 1e-9) return null;
  const angle = Math.atan2(vec.length(cross), vec.dot(from, to));
  if (angle < 1e-9) return null;
  return { center, axis, from, angle, radius };
}

function segmentLength(start: Vec3, end: Vec3, arc: ArcFrame | null): number {
  if (arc) return arc.radius * arc.angle;
  return vec.length(vec.sub(end, start));
}

/**
 * 両端と中点を常に含み、次の間隔が snap 以上のあいだだけ半分にする。
 * 1m / 0.5 は 0.5m で止める（1.02m のような少し長い梁も 0.25 には落とさない）。
 * `length=2, snap=0.5` → 0, 0.25, 0.5, 0.75, 1。
 */
export function braceSnapFractions(length: number, snap: number): number[] {
  const fractions = new Set([0, 0.5, 1]);
  if (!(length > 0) || !(snap > 0)) return [...fractions].sort((a, b) => a - b);
  let divisions = 2;
  while (length / (divisions * 2) >= snap - 1e-9) {
    divisions *= 2;
    const step = 1 / divisions;
    for (let index = 1; index < divisions; index += 2) fractions.add(index * step);
  }
  return [...fractions].sort((a, b) => a - b);
}

/**
 * 押し出し方向の格子。両端を含み、snap 間隔で切る（中点分割しない）。
 * `length=1.5, snap=0.5` → 0, 1/3, 2/3, 1（0, 0.5, 1.0, 1.5 m）。
 */
export function extrudeSnapFractions(length: number, snap: number): number[] {
  const fractions = new Set([0, 1]);
  if (!(length > 0) || !(snap > 0)) return [...fractions].sort((a, b) => a - b);
  const count = Math.floor(length / snap + 1e-9);
  for (let index = 1; index <= count; index++) {
    const t = (index * snap) / length;
    if (t < 1 - 1e-9) fractions.add(t);
  }
  return [...fractions].sort((a, b) => a - b);
}

export function interpolateBraceSegment(
  start: Vec3,
  end: Vec3,
  center: Vec3 | undefined,
  t: number
): Vec3 {
  const arc = segmentArc(start, end, center);
  if (!arc) return vec.add(start, vec.scale(vec.sub(end, start), t));
  return vec.add(arc.center, rotateAroundAxis(arc.from, arc.axis, arc.angle * t));
}

function segmentTangent(start: Vec3, end: Vec3, arc: ArcFrame | null, t: number): Vec3 {
  if (!arc) {
    const delta = vec.sub(end, start);
    const length = vec.length(delta);
    return length < 1e-9 ? { x: 1, y: 0, z: 0 } : vec.scale(delta, 1 / length);
  }
  const radial = rotateAroundAxis(arc.from, arc.axis, arc.angle * t);
  const tangent = vec.cross(arc.axis, radial);
  const length = vec.length(tangent);
  return length < 1e-9 ? vec.normalize(vec.sub(end, start)) : vec.scale(tangent, 1 / length);
}

function eachLogicalSegment(
  geometry: BraceGeometry,
  visit: (start: Vec3, end: Vec3, center: Vec3 | undefined) => void
): void {
  const { points, closed } = bracePolyline(geometry);
  const n = points.length;
  const count = closed ? n : n - 1;
  for (let index = 0; index < count; index++) {
    const start = points[index];
    const end = points[(index + 1) % n];
    if (vec.length(vec.sub(end, start)) < 1e-9) continue;
    visit(start, end, polylineArcCenter(geometry.arcs, index));
  }
}

export function braceSnapPoints(geometry: BraceGeometry, snap: number): Vec3[] {
  const points: Vec3[] = [];
  const offset = braceExtrudeOffset(geometry);
  const extruded = vec.length(offset) >= 1e-9;
  const alongExtrude = extruded ? extrudeSnapFractions(vec.length(offset), snap) : [0];

  eachLogicalSegment(geometry, (start, end, center) => {
    const arc = segmentArc(start, end, center);
    const length = segmentLength(start, end, arc);
    if (length < 1e-9) return;
    for (const t of braceSnapFractions(length, snap)) {
      const along = interpolateBraceSegment(start, end, center, t);
      for (const s of alongExtrude) {
        pushUnique(points, extruded ? vec.add(along, vec.scale(offset, s)) : along);
      }
    }
  });
  return points;
}

export function snapToBrace(geometry: BraceGeometry, point: Vec3, snap: number): Vec3 {
  const candidates = braceSnapPoints(geometry, snap);
  if (candidates.length === 0) return point;
  let best = candidates[0];
  let bestDist = vec.length(vec.sub(point, best));
  for (let index = 1; index < candidates.length; index++) {
    const candidate = candidates[index];
    const dist = vec.length(vec.sub(point, candidate));
    if (dist < bestDist) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

function sectionOffsets(axis: Vec3, rotation: number, width: number, height: number): Vec3[] {
  const { right, up } = braceCrossSectionBasis(axis, rotation);
  const hw = width / 2;
  const hh = height / 2;
  return [
    vec.add(vec.scale(right, -hw), vec.scale(up, -hh)),
    vec.add(vec.scale(right, hw), vec.scale(up, -hh)),
    vec.add(vec.scale(right, hw), vec.scale(up, hh)),
    vec.add(vec.scale(right, -hw), vec.scale(up, hh)),
  ];
}

function rotateOffsets(offsets: Vec3[], axis: Vec3, angle: number): Vec3[] {
  if (Math.abs(angle) < 1e-12) return offsets;
  return offsets.map((offset) => rotateAroundAxis(offset, axis, angle));
}

function pushRing(lines: BraceSnapLine[], ring: Vec3[]): void {
  for (let index = 0; index < ring.length; index++) {
    lines.push({ a: ring[index], b: ring[(index + 1) % ring.length] });
  }
}

function connectRings(lines: BraceSnapLine[], previous: Vec3[], next: Vec3[]): void {
  for (let index = 0; index < previous.length; index++) {
    lines.push({ a: previous[index], b: next[index] });
  }
}

function beamFrameLines(geometry: BraceGeometry, snap: number): BraceSnapLine[] {
  const lines: BraceSnapLine[] = [];
  const rotation = geometry.rotation ?? 0;
  const width = geometry.thickness;
  const height = geometry.thickness;

  eachLogicalSegment(geometry, (start, end, center) => {
    const arc = segmentArc(start, end, center);
    const length = segmentLength(start, end, arc);
    if (length < 1e-9) return;
    const fractions = braceSnapFractions(length, snap);
    const startTangent = segmentTangent(start, end, arc, 0);
    const baseOffsets = sectionOffsets(startTangent, rotation, width, height);
    let previous: Vec3[] | null = null;
    for (const t of fractions) {
      const origin = interpolateBraceSegment(start, end, center, t);
      const angle = arc ? arc.angle * t : 0;
      const offsets = arc ? rotateOffsets(baseOffsets, arc.axis, angle) : baseOffsets;
      const ring = offsets.map((offset) => vec.add(origin, offset));
      pushRing(lines, ring);
      if (previous) connectRings(lines, previous, ring);
      previous = ring;
    }
  });
  return lines;
}

/** 厚み 0 の帯から少し浮かせて、面に埋もれない緑格子にする。 */
export const STRIP_FRAME_PAD = ZERO_PLANE_THICKNESS / 2 + 0.004;

function stripFramePad(thickness: number): number {
  return effectivePlaneThickness(thickness) / 2 + 0.004;
}

function stripSegmentNormal(start: Vec3, end: Vec3, center: Vec3 | undefined, offset: Vec3): Vec3 {
  const tangent = segmentTangent(start, end, segmentArc(start, end, center), 0.5);
  return vec.normalize(vec.cross(tangent, offset));
}

function liftLine(line: BraceSnapLine, lift: Vec3): BraceSnapLine {
  return { a: vec.add(line.a, lift), b: vec.add(line.b, lift) };
}

function stripFrameLines(geometry: BraceGeometry, snap: number): BraceSnapLine[] {
  const lines: BraceSnapLine[] = [];
  const offset = braceExtrudeOffset(geometry);
  const alongExtrude = extrudeSnapFractions(vec.length(offset), snap);

  eachLogicalSegment(geometry, (start, end, center) => {
    const arc = segmentArc(start, end, center);
    const length = segmentLength(start, end, arc);
    if (length < 1e-9) return;
    const alongPath = braceSnapFractions(length, snap);
    const grid = alongPath.map((t) =>
      alongExtrude.map((s) =>
        vec.add(interpolateBraceSegment(start, end, center, t), vec.scale(offset, s))
      )
    );
    const onStrip: BraceSnapLine[] = [];
    for (let i = 0; i < alongPath.length; i++) {
      for (let j = 0; j < alongExtrude.length - 1; j++) {
        onStrip.push({ a: grid[i][j], b: grid[i][j + 1] });
      }
    }
    for (let j = 0; j < alongExtrude.length; j++) {
      for (let i = 0; i < alongPath.length - 1; i++) {
        onStrip.push({ a: grid[i][j], b: grid[i + 1][j] });
      }
    }
    const normal = stripSegmentNormal(start, end, center, offset);
    if (vec.length(normal) < 1e-9) {
      lines.push(...onStrip);
      return;
    }
    const lift = vec.scale(normal, stripFramePad(geometry.thickness));
    for (const line of onStrip) {
      lines.push(liftLine(line, lift), liftLine(line, vec.scale(lift, -1)));
    }
  });
  return lines;
}

export function braceSnapFrameLines(geometry: BraceGeometry, snap: number): BraceSnapLine[] {
  if (vec.length(braceExtrudeOffset(geometry)) >= 1e-9) return stripFrameLines(geometry, snap);
  return beamFrameLines(geometry, snap);
}
